import React from 'react';
import AspectRatioLockButton from './AspectRatioLockButton';

type LinkedDimensionsGroupProps = {
  /** When true, Width / Length / Height are treated as proportionally linked. */
  locked?: boolean;
  onToggleLock: () => void;
  widthField: React.ReactNode;
  lengthField: React.ReactNode;
  /** Optional third linked dimension (typically Height in 3D). */
  heightField?: React.ReactNode;
  /** Optional field beside Height (e.g. Rotation) — not aspect-linked. */
  trailingField?: React.ReactNode;
};

/**
 * Compact Metrics layout with an aspect-ratio lock between Width and Length.
 * When locked, a shared border/tint makes Width / Length / Height read as one
 * proportional group. Trailing fields (e.g. Rotation) stay outside that group.
 * Height always sits in a two-column row so it does not stretch full-bleed.
 */
const LinkedDimensionsGroup: React.FC<LinkedDimensionsGroupProps> = ({
  locked = false,
  onToggleLock,
  widthField,
  lengthField,
  heightField,
  trailingField,
}) => {
  const showTrailingBesideHeight = Boolean(heightField && trailingField && !locked);
  const showTrailingOutside = Boolean(trailingField && !showTrailingBesideHeight);

  return (
    <div
      className="space-y-1.5 py-0.5"
      data-aspect-ratio-locked={locked ? 'true' : 'false'}
    >
      <div
        className={
          locked
            ? 'rounded-sm border border-gray-300 bg-gray-50/90 p-1.5 ring-1 ring-inset ring-gray-200'
            : undefined
        }
      >
        <div className="grid grid-cols-[1fr_auto_1fr] items-end gap-x-1 gap-y-1.5">
          {widthField}
          <AspectRatioLockButton locked={locked} onToggle={onToggleLock} />
          {lengthField}
        </div>
        {heightField && (
          <div className="mt-1.5 grid grid-cols-2 items-end gap-2">
            {heightField}
            {showTrailingBesideHeight ? trailingField : null}
          </div>
        )}
      </div>
      {showTrailingOutside && (
        <div className="grid grid-cols-2 items-end gap-2">{trailingField}</div>
      )}
    </div>
  );
};

export default LinkedDimensionsGroup;
